import { gql } from '@apollo/client/core';

// Reusable fragment for glow fields
const glowFieldsFragment = `
  id
  slug
  entity
  entityId
  organization {
    id
    slug
    name
    avatar
    organization_type
  }
  user {
    id
    slug
    firstname
    lastname
    avatar
  }
  title
  description
  geolocation
  glowStartDate
  glowEndDate
  glowColor
  glowStartDateTimeZone
  glowEndDateTimeZone
  requireCtaToUnlock
  participationType
  hasParticipated
  showAmountRaised
  amountRaised
  showTotalVoices
  totalVoices
  showTotalViews
  totalViews
  totalShares
  status
  postEventMessage
  ctaStickerId
  ctaQuestionId
  ctaOrganizationId
  ctaSticker {
    id
    slug
    title
    url
    campaignType
    cost
    causes
    varients {
      id
      cost
    }
    sweepstakesSticker {
      id
    }
    quickCosts
  }
  ctaQuestion {
    id
    content
    entity
    entityId
  }
  ctaOrganization {
    id
    name
    avatar
    organization_type
    benevityId
    causeId
  }
`;

export const mutateGlow = gql`
mutation($orgId: String!, $input: GlowInput!) {
  mutateGlow(orgId: $orgId, input: $input) {
    id
    slug
    entity
    entityId
    organization {
      id
      name
      avatar
    }
    title
    description
    geolocation
    postEventMessage
    glowStartDate
    glowEndDate
    glowColor
    glowStartDateTimeZone
    glowEndDateTimeZone
    requireCtaToUnlock
    participationType
    showAmountRaised
    amountRaised
    showTotalVoices
    totalVoices
    showTotalViews
    totalViews
    totalShares
  }
}`;

export const findGlows = gql`
query GetGlows(
  $skip: Int,
  $take: Int,
  $sortAsc: Boolean,
  $sortBy: String,
  $searchText: String,
  $status: String,
  $orgId: String
) {
  getGlows(
    skip: $skip,
    take: $take,
    sortAsc: $sortAsc,
    sortBy: $sortBy,
    searchText: $searchText,
    status: $status,
    orgId: $orgId
  ) {
    ${glowFieldsFragment}
  }
}`;

export const findPastGlows = gql`
query GetPastGlows(
  $skip: Int,
  $take: Int,
  $sortAsc: Boolean,
  $sortBy: String,
  $searchText: String,
  $status: String,
  $orgId: String
) {
  getPastGlows(
    skip: $skip,
    take: $take,
    sortAsc: $sortAsc,
    sortBy: $sortBy,
    searchText: $searchText,
    status: $status,
    orgId: $orgId
  ) {
    ${glowFieldsFragment}
  }
}`;

export const findUpcomingGlows = gql`
query GetUpcomingGlows(
  $skip: Int,
  $take: Int,
  $sortAsc: Boolean,
  $sortBy: String,
  $searchText: String,
  $status: String,
  $orgId: String
) {
  getUpcomingGlows(
    skip: $skip,
    take: $take,
    sortAsc: $sortAsc,
    sortBy: $sortBy,
    searchText: $searchText,
    status: $status,
    orgId: $orgId
  ) {
    ${glowFieldsFragment}
  }
}`;

export const findGlow = (glowId: string, orgId?: string) => gql`
query {
  getGlow(idOrSlug: "${glowId}", isId: true${orgId ? `, orgId: "${orgId}"` : ''}) {
    id
    slug
    entity
    entityId
    organization {
      id
      name
      avatar
    }
    title
    description
    geolocation
    glowStartDate
    glowEndDate
    glowColor
    glowStartDateTimeZone
    glowEndDateTimeZone
    requireCtaToUnlock
    participationType
    ctaQuestionId
    showAmountRaised
    amountRaised
    showTotalVoices
    totalVoices
    showTotalViews
    totalViews
    totalShares
    status
    postEventMessage
    ctaSticker {
      id
      slug
      title
      url
      campaignType
    }
    ctaQuestion {
      id
      content
      createdAt
      entity
      entityId
    }
    ctaOrganization {
      id
      name
      avatar
    }
  }
}`;

export const mutateGlowStatus = gql`
mutation MutateGlowStatus($glowId: UUID!, $status: String!, $orgId: String!) {
  mutateGlowStatus(glowId: $glowId, status: $status, orgId: $orgId)
}`;

// Public Glow Statistics Query
export const getGlowsPublicStatistics = gql`
query GetGlowsPublicStatistics {
  getGlowsPublicStatistics {
    totalGlowsCount
    totalAmountRaised
    totalVoices
  }
}`;

// Public Upcoming Glows Query
export const getPublicUpcomingGlows = gql`
query GetPublicUpcomingGlows($skip: Int, $take: Int, $sortBy: String, $sortAsc: Boolean) {
  getPublicUpcomingGlows(skip: $skip, take: $take, sortBy: $sortBy, sortAsc: $sortAsc) {
    ${glowFieldsFragment}
  }
}`;

// Public Past Glows Query
export const getPublicPastGlows = gql`
query GetPublicPastGlows($skip: Int, $take: Int, $sortBy: String, $sortAsc: Boolean) {
  getPublicPastGlows(skip: $skip, take: $take, sortBy: $sortBy, sortAsc: $sortAsc) {
    ${glowFieldsFragment}
  }
}`;

// Get Public Glow Query (for public glow page)
export const getPublicGlow = gql`
query GetPublicGlow($idOrSlug: String!, $isId: Boolean!, $orgId: String, $participantEmail: String) {
  getPublicGlow(idOrSlug: $idOrSlug, isId: $isId, orgId: $orgId, participantEmail: $participantEmail) {
    ${glowFieldsFragment}
  }
}`;

// Increase Glow Share Count Mutation
export const increaseGlowShareCount = gql`
mutation IncreaseGlowShareCount($glowId: UUID!) {
  increaseGlowShareCount(glowId: $glowId)
}`;

// Increase Glow View Count Mutation
export const increaseGlowViewCount = gql`
mutation IncreaseGlowViewCount($glowId: UUID!) {
  increaseGlowViewCount(glowId: $glowId)
}`;
